import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import Chip from '@mui/material/Chip';
import { ReactNode } from 'react';
import {
  Feature,
  PlanInterval,
  ProductType,
  Subscription,
  SubscriptionMetadata,
  SubscriptionSource,
  TRIAL_SECONDARY_SOURCE,
} from '../../../models';
import { SubscriptionQuota } from './SubscriptionQuota';
import { useNavigate, useQuery } from '../../../routes';
import { plan } from '../../../layout/routes';
import { ProductBox } from '../../../components';
import { fromAccounts, fromSubscription } from '../../../store';
import { useAddLicenseHandler } from '../hooks/useAddLicenseHandler';
import getFeaturePrefix = Feature.getFeaturePrefix;

const DEFAULT_PRODUCT_QUOTA = 1;

const useStyle = makeStyles(theme => ({
  subscriptionBoxContent: {
    [theme.breakpoints.between('tablet', 'dsm')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.up('dsm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.down('tablet')]: {
      flexDirection: 'column',
    },
  },
}));

const AddMoreBtn = ({ children, onClick }: { children: ReactNode, onClick: () => void; }) => (
  <Button
    variant="contained"
    size="small"
    onClick={onClick}
    sx={{
      color: '#FFFFFF',
      backgroundColor: '#5959EB',
      '&:hover': {
        backgroundColor: '#43509B',
      },
    }}
  >
    {children}
  </Button>
);

export const SubscriptionBox = (
  { groupId, color, subscription }: {
    groupId: string;
    color: string;
    subscription: Subscription;
  },
) => {
  const classes = useStyle();
  const intl = useIntl();
  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const prefix = getFeaturePrefix(subscription.feature);
  const isTrial = subscription.source === SubscriptionSource.TRIAL_REQUEST
    && TRIAL_SECONDARY_SOURCE.includes(subscription.secondarySource);
  const isPromotionEligible = useSelector(fromAccounts.selectIsOptedInFirstThreeLicenses);
  const trueUpgradeInfo = useSelector(fromSubscription.selectTrueUpgradeInfo);
  // Use custom hook for add license logic
  const {
    handleAddLicense,
    AwsMarketplaceDialogComponent,
  } = useAddLicenseHandler();

  const navigateToPlan = () => {
    navigate(
      plan.path,
      replaceQuery({
        ...queryDictionary(),
        [`${prefix}Quota`]: DEFAULT_PRODUCT_QUOTA,
        [`${prefix}Cycle`]: subscription.source === SubscriptionSource.RECURLY
          ? subscription.billingInterval : PlanInterval.YEAR,
      }),
    );
  };

  const goToPlan = () => {
    handleAddLicense(navigateToPlan);
  };

  const renderSubProduct = (productNane: string, quantity: number) => (
    <Box
      key={`subscription-${productNane}`}
      display="flex"
      flexDirection="row"
      sx={theme => ({
        [theme.breakpoints.up('dsm')]: {
          justifyContent: 'space-between',
        },
        [theme.breakpoints.down('tablet')]: {
          justifyContent: 'space-between',
        },
      })}
    >
      <Typography
        variant="body2"
        minWidth={theme => theme.spacing(15)}
      >
        <FormattedMessage id={`feature.${productNane}.product.name`} />
      </Typography>
      <Typography
        variant="body2"
        fontWeight={400}
      >
        <FormattedMessage
          id="page.subscription.current.subscription.quota"
          values={{
            quota: quantity,
            unit: intl.formatMessage({ id: `feature.${productNane}.unit.plural` }),
          }}
        />
      </Typography>
    </Box>
  );

  const renderSubMetadata = (metadata: SubscriptionMetadata[]) => {
    if (metadata.length === 0) {
      return null;
    }

    return metadata.map(({ name, value }) => {
      const metadataName = name.toLowerCase();
      const metadataValue = Number(value);
      return renderSubProduct(metadataName, metadataValue);
    });
  };

  const renderMultiSubProduct = (
    subscription: Subscription,
  ) => {
    const { productType, feature, quota, metadata } = subscription;
    switch (productType) {
      case ProductType.TRUE_TEST:
      case ProductType.TRUE_PLATFORM:
        return (
          <>
            {renderSubProduct(feature.toLowerCase(), quota)}
            {metadata && renderSubMetadata(metadata)}
          </>
        );
      case ProductType.TESTOPS_G3:
        return (
          <>
            {metadata && renderSubMetadata(metadata)}
          </>
        );
      default:
        return renderSubProduct(feature.toLowerCase(), quota);
    }
  };

  const renderCurrentSubscription = (
    subscription: Subscription,
    hasMetadata: boolean | undefined,
  ) => (
    <Box
      display="flex"
      flexDirection={hasMetadata ? 'column' : 'row'}
      paddingBottom={1}
      width="100%"
    >
      <Typography
        variant="body1"
        fontWeight={400}
        fontSize={14}
      >
        <strong>
          <FormattedMessage id="page.subscription.current.subscription" />
        </strong>
        {!hasMetadata && (
          <FormattedMessage
            id="page.subscription.current.subscription.quota"
            values={{
              quota: subscription.quota,
              unit: intl.formatMessage({ id: `feature.${subscription.feature.toLowerCase()}.unit.plural` }),
            }}
          />
        )}
      </Typography>
      {hasMetadata && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          width="100%"
          paddingTop={1}
        >
          {renderMultiSubProduct(subscription)}
        </Box>
      )}
    </Box>
  );

  return (
    <ProductBox
      key={`subscription-group-${groupId}`}
      id={`subscription-group-${groupId}`}
      widthConfig={{ dsm: '100%' }}
    >
      <Box
        id={`product-group-${groupId}-header`}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          fontSize={14}
          color={color ?? '#727993'}
          textTransform="uppercase"
        >
          <FormattedMessage id={`product.group.${groupId}.title`} />
        </Typography>
        {isTrial && (
          <Chip
            label={intl.formatMessage({ id: 'page.subscription.current.subscription.trial' })}
            size="small"
            sx={{
              backgroundColor: '#5959EB',
              color: '#FFFFFF',
              fontWeight: 600,
              height: 24,
              fontSize: 12,
              borderRadius: 4,
            }}
          />
        )}
      </Box>
      <Box
        id={`subscription-type-${subscription.feature.toLowerCase()}`}
        key={`subscription-type-${subscription.feature.toLowerCase()}`}
        display="flex"
        width="100%"
        className={classes.subscriptionBoxContent}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          sx={theme => ({
            [theme.breakpoints.down('dsm')]: {
              width: '65%',
            },
            [theme.breakpoints.up('dsm')]: {
              width: '100%',
            },
            [theme.breakpoints.down('tablet')]: {
              width: '100%',
            },
          })}
        >
          <Box
            display="flex"
            flexDirection="row"
            alignContent="center"
            alignItems="center"
            paddingBottom={1}
            height={theme => theme.spacing(6)}
          >
            <Typography
              variant="body1"
              fontWeight={600}
              fontSize={18}
            >
              <FormattedMessage
                id={`feature.${subscription.feature.toLowerCase()}.product.name`}
              />
            </Typography>
          </Box>
          {renderCurrentSubscription(
            subscription,
            subscription.metadata && subscription.metadata.length > 0,
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          sx={theme => ({
            [theme.breakpoints.down('dsm')]: {
              width: '35%',
            },
            [theme.breakpoints.up('dsm')]: {
              width: '100%',
            },
            [theme.breakpoints.down('tablet')]: {
              width: '100%',
            },
          })}
        >
          <SubscriptionQuota subscription={subscription} />
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        justifySelf="self-end"
        paddingTop={2}
        sx={theme => ({
          [theme.breakpoints.between('tablet', 'dsm')]: {
            width: 154,
          },
        })}
      >
        {isTrial && isPromotionEligible
          ? (
            <Tooltip
              title={(
                <FormattedMessage id="page.subscription.current.subscription.manage.trial.offer.detail" />
              )}
              placement="top"
            >
              <div>
                <AddMoreBtn onClick={goToPlan}>
                  <FormattedMessage id="page.subscription.current.subscription.manage.trial.offer" />
                </AddMoreBtn>
              </div>
            </Tooltip>
          )
          : !trueUpgradeInfo?.reserved && (
          <AddMoreBtn onClick={goToPlan}>
            <FormattedMessage
              id="page.subscription.current.subscription.manage.add.more"
              values={{
                unit: intl.formatMessage({ id: `feature.${subscription.feature.toLowerCase()}.unit` }),
              }}
            />
          </AddMoreBtn>
          )}
      </Box>
      <AwsMarketplaceDialogComponent />
    </ProductBox>
  );
};
